'use client'

import { useCallback, useEffect, useRef } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useSession } from '@marketplace-web/shared/session-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import {
  getVasEntryPoints,
  getVasEntryPointsSvcVas,
  transformVasEntryPointsResponse,
  VasEntryPoint,
} from '@marketplace-web/vas/vas-data'

const useFetchVasEntryPoints = (entryPointNames: Array<VasEntryPoint>, isDisabled?: boolean) => {
  const { user: currentUser } = useSession()
  const trackExpose = useTrackAbTestCallback()
  const vasApiGatewaySwapEntryPointsAbTest = useAbTest('vas_api_gateway_swap_entry_points')
  const isVasApiGatewaySwapEntryPointsAbTestOn =
    vasApiGatewaySwapEntryPointsAbTest?.variant === 'on'

  const {
    fetch: fetchVasEntryPointsCore,
    transformedData: dataCore,
    isLoading: isLoadingCore,
    error: errorCore,
  } = useFetch(getVasEntryPoints, transformVasEntryPointsResponse)
  const isClosetPromotionKillswitchEnabled = useFeatureSwitch('killswitch_closet_promotion_web')

  const {
    fetch: fetchVasEntryPointsSvcVas,
    transformedData: dataSvcVas,
    isLoading: isLoadingSvcVas,
    error: errorSvcVas,
  } = useFetch(getVasEntryPointsSvcVas, transformVasEntryPointsResponse)

  const data = isVasApiGatewaySwapEntryPointsAbTestOn ? dataSvcVas : dataCore
  const isLoading = isVasApiGatewaySwapEntryPointsAbTestOn ? isLoadingSvcVas : isLoadingCore
  const error = isVasApiGatewaySwapEntryPointsAbTestOn ? errorSvcVas : errorCore
  const fetchVasEntryPoints = isVasApiGatewaySwapEntryPointsAbTestOn
    ? fetchVasEntryPointsSvcVas
    : fetchVasEntryPointsCore

  const filteredEntryPointNamesRef = useRef<Array<VasEntryPoint>>(
    isClosetPromotionKillswitchEnabled
      ? entryPointNames.filter(entryPoint => entryPoint !== VasEntryPoint.PromotedClosets)
      : entryPointNames,
  )

  const fetchClosetPromotionEntryPoint = useCallback(async () => {
    if (isClosetPromotionKillswitchEnabled) return undefined
    if (!currentUser) return undefined

    const { transformedData: vasEntryPoints } = await fetchVasEntryPoints({
      bannerNames: [VasEntryPoint.PromotedClosets],
    })

    trackExpose(vasApiGatewaySwapEntryPointsAbTest)

    const closetPromoBanner = vasEntryPoints?.find(
      entryPoint => entryPoint.name === VasEntryPoint.PromotedClosets,
    )

    return closetPromoBanner
  }, [
    isClosetPromotionKillswitchEnabled,
    currentUser,
    vasApiGatewaySwapEntryPointsAbTest,
    fetchVasEntryPoints,
    trackExpose,
  ])

  useEffect(() => {
    if (!currentUser) return
    if (!filteredEntryPointNamesRef.current.length) return
    if (isDisabled) return

    fetchVasEntryPoints({ bannerNames: filteredEntryPointNamesRef.current })
    trackExpose(vasApiGatewaySwapEntryPointsAbTest)
  }, [
    fetchVasEntryPoints,
    trackExpose,
    isDisabled,
    filteredEntryPointNamesRef,
    vasApiGatewaySwapEntryPointsAbTest,
    currentUser,
  ])

  return {
    data,
    isLoading,
    error,
    fetchClosetPromotionEntryPoint,
  }
}

export default useFetchVasEntryPoints
